
import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import './ConsultationCalculatorSection.css';
import axios from 'axios';

const API_BASE = "http://localhost:5000/api";

const ConsultationCalculatorSection = () => {
  const [activeTab, setActiveTab] = useState('residential');
  const [formData, setFormData] = useState({
    // Residential
    fullName: '',
    whatsappNumber: '',
    monthlyBill: '',
    pincode: '',
    city: '',
    
    // Commercial/Industrial
    firmName: '',
    email: ''
  });
  
  const [calculatorData, setCalculatorData] = useState({
    name: '',
    phone: '',
    roofArea: '',
    monthlyBill: '',
    location: 'Mumbai'
  });
  
  const [calculatedAmount, setCalculatedAmount] = useState(null);
  const { isLoggedIn } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleTabHover = (tab) => {
    setActiveTab(tab);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCalculatorChange = (e) => {
    const { name, value } = e.target;
    setCalculatorData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculateSavings = () => {
    const { roofArea, monthlyBill } = calculatorData;
    const baseCost = 1500; // per sq ft
    const billSavings = parseFloat(monthlyBill) * 0.7;
    const areaCost = parseFloat(roofArea) * baseCost;
    const totalSavings = areaCost + (billSavings * 12 * 10); // 10 years
    
    if (isLoggedIn) {
      return totalSavings.toLocaleString('en-IN');
    } else {
      const strAmount = Math.floor(totalSavings).toString();
      const maskedAmount = strAmount.substring(0, 2) + '*'.repeat(strAmount.length - 2);
      return '₹' + maskedAmount;
    }
  };

  const handleCalculatorSubmit = (e) => {
    e.preventDefault();
    const amount = calculateSavings();
    setCalculatedAmount(amount);
    
    // Auto-submit calculator data when user is logged in
    if (isLoggedIn) {
      handleSaveCalculatorData(amount);
    }
  };

  const handleSaveCalculatorData = async (amount) => {
    try {
      const consultationData = {
        formType: 'residential',
        name: calculatorData.name,
        phone: calculatorData.phone,
        whatsappNumber: calculatorData.phone,
        monthlyBill: calculatorData.monthlyBill,
        pincode: '',
        city: calculatorData.location,
        roofArea: calculatorData.roofArea,
        location: calculatorData.location,
        estimatedSavings: amount,
        source: 'savings_calculator'
      };
      
      await axios.post(`${API_BASE}/consultations/submit`, consultationData);
      alert('Calculator data saved!');
    } catch (error) {
      console.error('Error saving calculator data:', error);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Prepare consultation data
      const consultationData = {
        formType: activeTab,
        monthlyBill: formData.monthlyBill,
        pincode: formData.pincode,
        city: formData.city,
        source: 'consultation_calculator'
      };

      // Add fields based on tab type
      if (activeTab === 'residential') {
        consultationData.name = formData.fullName;
        consultationData.whatsappNumber = formData.whatsappNumber;
        consultationData.phone = formData.whatsappNumber;
      } else {
        consultationData.firmName = formData.firmName;
        consultationData.email = formData.email;
        consultationData.name = formData.firmName; // Use firm name as name for commercial/industrial
      }

      // Add calculator data if available
      if (calculatorData.roofArea) {
        consultationData.roofArea = calculatorData.roofArea;
      }
      if (calculatorData.location) {
        consultationData.location = calculatorData.location;
      }
      if (calculatedAmount) {
        consultationData.estimatedSavings = calculatedAmount;
      }

      console.log('Submitting consultation data:', consultationData);

      const response = await axios.post(`${API_BASE}/consultations/submit`, consultationData);
      
      if (response.data.success) {
        setSubmitMessage('✅ Consultation submitted successfully! We will contact you soon.');
        
        // Reset form
        setFormData({
          fullName: '',
          whatsappNumber: '',
          monthlyBill: '',
          pincode: '',
          city: '',
          firmName: '',
          email: ''
        });
        
        // Reset calculator data
        setCalculatorData({
          name: '',
          phone: '',
          roofArea: '',
          monthlyBill: '',
          location: 'Mumbai'
        });
        
        setCalculatedAmount(null);
        
        // Show success message
        alert('Consultation form submitted! We will contact you soon.');
      }
    } catch (error) {
      console.error('Error submitting consultation:', error);
      setSubmitMessage('❌ Failed to submit. Please try again.');
      alert('Failed to submit consultation. Please try again.');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitMessage(''), 5000);
    }
  };

  const billRanges = {
    residential: [
      { label: '0 - ₹15000', value: '0-15000' },
      { label: '₹15000 - ₹25000', value: '15000-25000' },
      { label: '₹25000 - ₹50000', value: '25000-50000' },
      { label: '> ₹50000', value: '50000+' }
    ],
    commercial: [
      { label: '0 - ₹50000', value: '0-50000' },
      { label: '₹50000 - 1Lacs', value: '50000-100000' },
      { label: '1Lacs - 2Lacs', value: '100000-200000' },
      { label: '> ₹2Lacs', value: '200000+' }
    ],
    industrial: [
      { label: '0 - ₹2500', value: '0-2500' },
      { label: '₹2500 - ₹5000', value: '2500-5000' },
      { label: '₹5000 - ₹10000', value: '5000-10000' },
      { label: '> ₹10000', value: '10000+' }
    ]
  };

  const currentBillRanges = billRanges[activeTab];

  return (
    <div className="consultation-calculator-wrapper">
      {/* Consultation Form Section (Left) */}
      <div className="consultation-section">
        <div className="section-header">
          <h2 className="section-title">CONSULTATION</h2>
          <p className="section-subtitle">Reach out for a FREE consultation today</p>
          <p className="section-description">
            Let our solar experts provide you with trustworthy insights and get advice tailored to your solar needs!
          </p>
        </div>

        {/* Tabs for Residential/Commercial/Industrial */}
        <div className="project-tabs">
          <div 
            className={`project-tab ${activeTab === 'residential' ? 'active' : ''}`}
            onMouseEnter={() => handleTabHover('residential')}
            onClick={() => setActiveTab('residential')}
          >
            Residential
          </div>
          <div 
            className={`project-tab ${activeTab === 'commercial' ? 'active' : ''}`}
            onMouseEnter={() => handleTabHover('commercial')}
            onClick={() => setActiveTab('commercial')}
          >
            Commercial
          </div>
          <div 
            className={`project-tab ${activeTab === 'industrial' ? 'active' : ''}`}
            onMouseEnter={() => handleTabHover('industrial')}
            onClick={() => setActiveTab('industrial')}
          >
            Industrial
          </div>
        </div>

        {/* Form */}
        <form className="consultation-form" onSubmit={handleFormSubmit}>
          {activeTab === 'residential' ? (
            <>
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleFormChange}
                  placeholder="Enter your full name"
                  required
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="form-group">
                <label>Whatsapp Number *</label>
                <input
                  type="tel"
                  name="whatsappNumber"
                  value={formData.whatsappNumber}
                  onChange={handleFormChange}
                  placeholder="+91 9876543210"
                  required
                  disabled={isSubmitting}
                />
              </div>
            </>
          ) : (
            <>
              <div className="form-group">
                <label>Name of Firm *</label>
                <input
                  type="text"
                  name="firmName"
                  value={formData.firmName}
                  onChange={handleFormChange}
                  placeholder="Enter firm name"
                  required
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  placeholder="your@email.com"
                  required
                  disabled={isSubmitting}
                />
              </div>
            </>
          )}

          <div className="form-group">
            <label>Monthly Electricity Bill *</label>
            <div className="bill-range-buttons">
              {currentBillRanges.map((range, index) => (
                <button
                  key={index}
                  type="button"
                  className={`range-btn ${formData.monthlyBill === range.value ? 'active' : ''}`}
                  onClick={() => setFormData(prev => ({ ...prev, monthlyBill: range.value }))}
                  disabled={isSubmitting}
                >
                  {range.label}
                </button>
              ))}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Pin Code *</label>
              <input
                type="text"
                name="pincode"
                value={formData.pincode}
                onChange={handleFormChange}
                placeholder="400001"
                required
                disabled={isSubmitting}
              />
            </div>
            <div className="form-group">
              <label>City *</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleFormChange}
                placeholder="Mumbai"
                required
                disabled={isSubmitting}
              />
            </div>
          </div>

          {submitMessage && (
            <div className={`submit-message ${submitMessage.includes('✅') ? 'success' : 'error'}`}>
              {submitMessage}
            </div>
          )}

          <button type="submit" className="submit-btn" disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
        </form>
      </div>

      {/* Calculator Section (Right) */}
      <div className="calculator-section">
        <div className="calculator-header">
          <h2 className="calculator-title">Calculate Your Solar Savings</h2>
          <p className="calculator-subtitle">Get personalized estimates for your solar installation</p>
          <div className="divider"></div>
        </div>

        <form className="calculator-form" onSubmit={handleCalculatorSubmit}>
          <div className="form-group">
            <label>Full Name</label>
            <input
              type="text"
              name="name"
              value={calculatorData.name}
              onChange={handleCalculatorChange}
              placeholder="User Name"
            />
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Phone Number</label>
              <input
                type="tel"
                name="phone"
                value={calculatorData.phone}
                onChange={handleCalculatorChange}
                placeholder="1234567891"
              />
            </div>
            <div className="form-group">
              <label>Roof Area (sq ft)</label>
              <input
                type="number"
                name="roofArea"
                value={calculatorData.roofArea}
                onChange={handleCalculatorChange}
                placeholder="100"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Monthly Bill (₹)</label>
              <input
                type="number"
                name="monthlyBill"
                value={calculatorData.monthlyBill}
                onChange={handleCalculatorChange}
                placeholder="1000"
              />
            </div>
            <div className="form-group">
              <label>Location</label>
              <select
                name="location"
                value={calculatorData.location}
                onChange={handleCalculatorChange}
              >
                <option value="Mumbai">Mumbai</option>
                <option value="Pune">Pune</option>
                <option value="Nagpur">Nagpur</option>
                <option value="Nashik">Nashik</option>
                <option value="Aurangabad">Aurangabad</option>
              </select>
            </div>
          </div>

          <button type="submit" className="calculate-btn">
            Calculate Savings
          </button>
        </form>

        {calculatedAmount && (
          <div className="calculator-results">
            <h3>Your Estimated Savings</h3>
            <div className="result-amount">
              ₹{calculatedAmount}
            </div>
            {!isLoggedIn && (
              <div className="login-prompt">
                Login to see full amount
              </div>
            )}
            <div className="result-note">
              Estimated savings over 10 years
            </div>
          </div>
        )}

        <div className="calculator-features">
          <div className="feature">
            <div className="feature-icon">✓</div>
            <div className="feature-text">Personalized Calculation</div>
          </div>
          <div className="feature">
            <div className="feature-icon">✓</div>
            <div className="feature-text">Accurate Estimates</div>
          </div>
          <div className="feature">
            <div className="feature-icon">✓</div>
            <div className="feature-text">Free Consultation</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultationCalculatorSection;